<script>
import FormSelect from "@/components/forms/FormSelect.vue"
import { useEndpointsStore } from "@/store/modules/endpoints"
import DataFlowDiagramEndpointBox from "@/components/groups/diagram/DataFlowDiagramEndpointBox.vue"
import NewEndpointModal from "@/components/modals/endpoints/new.vue"

import axios from "axios"

export default {
  name: "DataFlowDiagramEndpoints",
  components: { DataFlowDiagramEndpointBox, FormSelect, NewEndpointModal },
  props: {
    dataFlow: {
      type: Object,
      required: true
    },
    isActive: {
      type: Boolean,
      required: true
    },
    showHeader: {
      type: Boolean,
      default: true
    },
    showEditActions: {
      type: Boolean,
      default: true
    }
  },
  emits: ["add", "remove"],
  setup() {
    const endpointsStore = useEndpointsStore()
    return { endpointsStore }
  },
  data: () => ({
    hookHeight: "0",
    isNewEndpointOpened: false,
    isSelectShown: false
  }),
  computed: {
    httpEndpointList() {
      return this.endpointsStore.allEndpoints
    },
    mqttGatewaysResponse() {
      return this.endpointsStore.mqttGatewaysResponse
    },
    availableEndpointList() {
      const assignedEndpointIdArray = this.dataFlow.endpoints.map((end) => end.custDestId)
      return [...this.httpEndpointList, ...this.mqttGatewaysResponse.data].filter(
        (end) => !assignedEndpointIdArray.includes(end.custDestId)
      )
    }
  },
  watch: {
    "dataFlow.endpoints": {
      handler(endpointsList) {
        if (endpointsList.length) {
          // nejdřív se musí komponenty vyrenderovat, ani nextTick nestačil, tak dávám timeout
          setTimeout(this.calculateHookHeight, 500)
        }
      },
      immediate: true
    }
  },
  created() {
    this.endpointsStore.getAllEndpoints({ sort: "custDestName", rowsPerPage: 1000, currentPage: 0 })
    this.endpointsStore.getMqttGateways({ rowsPerPage: 1000, currentPage: 0 })
  },
  methods: {
    calculateHookHeight() {
      const endpointBoxes = document.querySelectorAll("[data-type-endpoints] .data-flow-diagram__box__option")

      // Need at least two boxes to measure the span between them. The timeout
      // that calls this can fire after the diagram is gone (e.g. browser-back),
      // leaving endpointBoxes empty — guard so we don't read getBoundingClientRect
      // on undefined.
      if (endpointBoxes.length < 2) {
        return "0"
      }

      const box1 = endpointBoxes[0].getBoundingClientRect()
      const box2 = endpointBoxes[endpointBoxes.length - 1].getBoundingClientRect()

      this.hookHeight = `${box2.top - box1.top - 36}px`
    },
    async handleEndpointAdded({ endpointId, protocol }) {
      this.isNewEndpointOpened = false

      let endpoint

      if (protocol === "HTTP") {
        const response = await axios.get(`${process.env.VUE_APP_API}/http/endpoints/${endpointId}`)
        endpoint = response.data.data
      }
      if (protocol === "MQTT") {
        const response = await axios.get(`${process.env.VUE_APP_API}/mqtt/gateways/${endpointId}`)
        endpoint = response.data.data[0]
      }

      this.dataFlow.endpoints = this.dataFlow.endpoints.concat({ ...endpoint, protocol })
    },
    handleEndpointSelected(endpoint) {
      this.isSelectShown = false
      this.$emit("add", endpoint)
    }
  }
}
</script>

<template>
  <div
    :class="{ 'data-flow-diagram__box--inactive': !isActive, 'pt-0': !showHeader }"
    class="data-flow-diagram__box"
    data-type-endpoints
  >
    <div v-if="showHeader" class="data-flow-diagram__box__header">
      {{ $t("ENDPOINTS_COL_NEW_DF") }}
      <a href="" @click.prevent="() => (isActive ? (isNewEndpointOpened = true) : {})">
        {{ $t("NEW_ENDPOINT") }}
      </a>
    </div>
    <div class="position-relative">
      <div v-if="dataFlow.endpoints.length" :style="{ height: hookHeight }" class="data-flow-diagram__green-hook" />

      <div class="data-flow-diagram__box__option mb-3">
        <div class="d-flex justify-content-between">
          <div class="data-flow-diagram__box__option__header">
            <div class="atom-icon">
              <img alt="" src="@/assets/icons/icon_cra_cloud.svg" />
            </div>
            <h6 class="mb-0">
              {{ $t("CRA_CLOUD") }}
            </h6>
          </div>
          <div class="atom-icon">
            <img alt="" src="@/assets/icons/icon_locked.svg" />
          </div>
        </div>
        <div>
          {{ $t("DATA_ARE_ALWAYS_SAVED_ON_CRA_CLOUD") }}
        </div>
      </div>

      <div
        v-for="endpoint in dataFlow.endpoints"
        :key="`endpointDFBox${endpoint.custDestId}`"
        class="data-flow-diagram__box__option mb-3 position-relative"
      >
        <DataFlowDiagramEndpointBox
          :data-flow="dataFlow"
          :endpoint="endpoint"
          :show-edit-actions="showEditActions"
          @remove="$emit('remove', endpoint)"
        />
      </div>

      <div>
        <a
          v-if="showEditActions"
          v-show="!isSelectShown"
          href=""
          @click.prevent="() => (isActive ? (isSelectShown = true) : {})"
        >
          {{ $t("ASSIGN_NEXT_ENDPOINT") }}
        </a>

        <FormSelect
          v-show="isSelectShown"
          :items="availableEndpointList"
          :placeholder="$t('SELECT_FROM_ENDPOINT_LIST')"
          :value="null"
          class="data-flow-diagram-endpoints-select"
          item-subtext="protocolLabel"
          item-text="custDestName"
          item-value="custDestId"
          label=""
          large
          @change="handleEndpointSelected"
        />
      </div>
    </div>

    <NewEndpointModal
      :opened="isNewEndpointOpened ? ['newEndpoint'] : []"
      :show-data-flows="false"
      protocol=""
      @saved="handleEndpointAdded"
      @close-box="isNewEndpointOpened = false"
    />
  </div>
</template>
